/**
 * Driver path resolution — maps execution driver names to their runtime paths.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Resolved runtime path structure for a bridge driver.
 * All three directories are relative to the project root.
 * @docLink packages/core/concepts#driver-paths
 */
export interface DriverPaths {
  /** Where skills (SKILL.md dirs) are installed relative to projectDir */
  skillsDir: string;
  /** Where agent definitions are installed relative to projectDir */
  agentsDir: string;
  /** Where prompt files are installed relative to projectDir */
  promptsDir: string;
}

// ── Driver defaults ───────────────────────────────────────────────────────────

/**
 * Built-in per-driver runtime path conventions (relative to project root).
 * Covers `claude-sdk`, `codex`, and `omp`. Used as defaults by `resolveDriverPaths`.
 * @docLink packages/core/concepts#driver-defaults
 */
export const DRIVER_DEFAULTS: Record<string, DriverPaths> = {
  "claude-sdk": {
    skillsDir: ".claude/skills",
    agentsDir: ".claude/agents",
    promptsDir: ".claude/commands",
  },
  codex: {
    skillsDir: ".codex/skills",
    agentsDir: ".codex/agents",
    promptsDir: ".codex/rules",
  },
  omp: {
    skillsDir: ".omp/skills",
    agentsDir: ".omp/agents",
    promptsDir: ".omp/prompts",
  },
};

// ── Driver resolution ─────────────────────────────────────────────────────────

/** Settings interface — minimal subset needed for driver path resolution. */
interface SettingsLike {
  skillsDir?: string;
  agentsDir?: string;
  promptsDir?: string;
  driver?: string;
}

/**
 * Resolve the effective runtime paths for a bridge driver.
 *
 * Looks up `DRIVER_DEFAULTS[settings.driver]`, then overlays any per-field
 * overrides from `settings.skillsDir`, `settings.agentsDir`, `settings.promptsDir`.
 * Falls back to the `omp` defaults for unknown driver values.
 *
 * To look up file install paths for a specific IDE/tool target, use
 * `DRIVER_TARGETS[driverTarget]` from `driver-targets.ts` directly.
 *
 * @param settings - Minimal settings object providing `driver` and optional path overrides
 * @returns `DriverPaths` with resolved relative paths for skills, agents, and prompts
 * @docLink packages/core/concepts#resolve-driver-paths
 */
export function resolveDriverPaths(settings: SettingsLike): DriverPaths {
  const driver = settings.driver ?? "omp";
  const defaults = DRIVER_DEFAULTS[driver] ?? DRIVER_DEFAULTS.omp;
  return {
    skillsDir: settings.skillsDir ?? defaults!.skillsDir,
    agentsDir: settings.agentsDir ?? defaults!.agentsDir,
    promptsDir: settings.promptsDir ?? defaults!.promptsDir,
  };
}
